import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { teacherApi } from '../lib/api';
import api from '../lib/api';
import {
  Search,
  Plus,
  MoreHorizontal,
  Eye,
  Edit,
  Phone,
  Mail,
  BookOpen,
  GraduationCap,
  Users,
  UserPlus,
  UserCheck,
  UserX,
  Key,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

export default function TeachersPage() {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [accountStatuses, setAccountStatuses] = useState({});
  const [actionLoading, setActionLoading] = useState({});
  const [newTeacher, setNewTeacher] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    teacher_type: 'quranic',
    specialization: '',
  });

  useEffect(() => {
    fetchTeachers();
  }, [filterType]);

  const fetchTeachers = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType !== 'all') {
        params.teacher_type = filterType;
      }
      const response = await teacherApi.getAll(params);
      setTeachers(response.data);
      
      // Récupérer le statut de compte pour chaque enseignant
      const statuses = {};
      await Promise.all(
        response.data.map(async (teacher) => {
          try {
            const statusRes = await api.get(`/teachers/${teacher.id}/account-status`);
            statuses[teacher.id] = statusRes.data;
          } catch {
            statuses[teacher.id] = { has_account: false };
          }
        })
      );
      setAccountStatuses(statuses);
    } catch (error) {
      toast.error('Erreur lors du chargement des enseignants');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async (teacherId) => {
    setActionLoading(prev => ({ ...prev, [teacherId]: true }));
    try {
      const res = await api.post(`/teachers/${teacherId}/create-account`);
      toast.success(`Compte créé ! Email: ${res.data.email}, Mot de passe: prof123`);
      setAccountStatuses(prev => ({
        ...prev,
        [teacherId]: { has_account: true, is_active: true, email: res.data.email }
      }));
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la création du compte';
      toast.error(message);
    } finally {
      setActionLoading(prev => ({ ...prev, [teacherId]: false }));
    }
  };

  const handleToggleAccount = async (teacherId) => {
    setActionLoading(prev => ({ ...prev, [teacherId]: true }));
    try {
      const res = await api.post(`/teachers/${teacherId}/toggle-account`);
      toast.success(res.data.message);
      setAccountStatuses(prev => ({
        ...prev,
        [teacherId]: { ...prev[teacherId], is_active: res.data.is_active }
      }));
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la modification';
      toast.error(message);
    } finally {
      setActionLoading(prev => ({ ...prev, [teacherId]: false }));
    }
  };

  const handleAddTeacher = async (e) => {
    e.preventDefault();
    if (!newTeacher.first_name || !newTeacher.last_name || !newTeacher.phone) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }

    setFormLoading(true);
    try {
      await teacherApi.create(newTeacher);
      toast.success('Enseignant ajouté avec succès');
      setShowAddDialog(false);
      setNewTeacher({
        first_name: '',
        last_name: '',
        phone: '',
        email: '',
        teacher_type: 'quranic',
        specialization: '',
      });
      fetchTeachers();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    } finally {
      setFormLoading(false);
    }
  };

  const filteredTeachers = teachers.filter((teacher) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      teacher.first_name.toLowerCase().includes(searchLower) ||
      teacher.last_name.toLowerCase().includes(searchLower) ||
      (teacher.phone && teacher.phone.includes(searchLower))
    );
  });

  const getTeacherTypeBadge = (type) => {
    const badges = {
      quranic: { label: 'Coranique', class: 'badge-quranic', icon: BookOpen },
      classical: { label: 'Classique', class: 'badge-classical', icon: GraduationCap },
      mixed: { label: 'Mixte', class: 'badge-mixed', icon: Users },
    };
    return badges[type] || { label: type, class: '', icon: Users };
  };

  const stats = {
    total: teachers.length,
    quranic: teachers.filter((t) => t.teacher_type === 'quranic').length,
    classical: teachers.filter((t) => t.teacher_type === 'classical').length,
    mixed: teachers.filter((t) => t.teacher_type === 'mixed').length,
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="teachers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">Enseignants</h1>
          <p className="text-muted-foreground text-sm">Gérez le corps enseignant</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="add-teacher-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouvel enseignant
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Ajouter un enseignant</DialogTitle>
              <DialogDescription>Renseignez les informations de l'enseignant</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddTeacher} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">Prénom *</Label>
                  <Input
                    id="first_name"
                    value={newTeacher.first_name}
                    onChange={(e) => setNewTeacher({ ...newTeacher, first_name: e.target.value })}
                    placeholder="Prénom"
                    data-testid="teacher-firstname"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Nom *</Label>
                  <Input
                    id="last_name"
                    value={newTeacher.last_name}
                    onChange={(e) => setNewTeacher({ ...newTeacher, last_name: e.target.value })}
                    placeholder="Nom"
                    data-testid="teacher-lastname"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Téléphone *</Label>
                <Input
                  id="phone"
                  value={newTeacher.phone}
                  onChange={(e) => setNewTeacher({ ...newTeacher, phone: e.target.value })}
                  placeholder="+221 77 123 45 67"
                  data-testid="teacher-phone"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={newTeacher.email}
                  onChange={(e) => setNewTeacher({ ...newTeacher, email: e.target.value })}
                  placeholder="email@exemple.com"
                  data-testid="teacher-email"
                />
              </div>
              <div className="space-y-2">
                <Label>Type d'enseignement *</Label>
                <Select
                  value={newTeacher.teacher_type}
                  onValueChange={(value) => setNewTeacher({ ...newTeacher, teacher_type: value })}
                >
                  <SelectTrigger data-testid="teacher-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quranic">Coranique</SelectItem>
                    <SelectItem value="classical">Classique</SelectItem>
                    <SelectItem value="mixed">Mixte</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="specialization">Spécialisation</Label>
                <Input
                  id="specialization"
                  value={newTeacher.specialization}
                  onChange={(e) => setNewTeacher({ ...newTeacher, specialization: e.target.value })}
                  placeholder="Tajwid, Mathématiques..."
                  data-testid="teacher-specialization"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                  Annuler
                </Button>
                <Button type="submit" disabled={formLoading} data-testid="save-teacher-btn">
                  {formLoading ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-hover">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold font-heading">{stats.total}</p>
              <p className="text-xs text-muted-foreground">Total</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold font-heading">{stats.quranic}</p>
              <p className="text-xs text-muted-foreground">Coraniques</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-secondary" />
            </div>
            <div>
              <p className="text-2xl font-bold font-heading">{stats.classical}</p>
              <p className="text-xs text-muted-foreground">Classiques</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-accent" />
            </div>
            <div>
              <p className="text-2xl font-bold font-heading">{stats.mixed}</p>
              <p className="text-xs text-muted-foreground">Mixtes</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher par nom ou téléphone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 rounded-full"
                data-testid="search-teachers"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full sm:w-48 rounded-full" data-testid="filter-teacher-type">
                <SelectValue placeholder="Filtrer par type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                <SelectItem value="quranic">Coraniques</SelectItem>
                <SelectItem value="classical">Classiques</SelectItem>
                <SelectItem value="mixed">Mixtes</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Teachers Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <Skeleton className="w-12 h-12 rounded-full" />
                  <div>
                    <Skeleton className="h-4 w-32 mb-2" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredTeachers.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <GraduationCap className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
            <p className="text-muted-foreground mb-4">Aucun enseignant trouvé</p>
            <Button variant="outline" className="rounded-full" onClick={() => setShowAddDialog(true)}>
              Ajouter un enseignant
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTeachers.map((teacher) => {
            const typeBadge = getTeacherTypeBadge(teacher.teacher_type);
            const TypeIcon = typeBadge.icon;
            const accountStatus = accountStatuses[teacher.id] || {};
            const isActionLoading = actionLoading[teacher.id];
            return (
              <Card key={teacher.id} className="card-hover" data-testid={`teacher-card-${teacher.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-lg font-medium text-primary">
                          {teacher.first_name[0]}{teacher.last_name[0]}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-medium text-foreground">
                          {teacher.first_name} {teacher.last_name}
                        </h3>
                        <Badge variant="outline" className={cn('text-xs mt-1', typeBadge.class)}>
                          <TypeIcon className="w-3 h-3 mr-1" />
                          {typeBadge.label}
                        </Badge>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem>
                          <Eye className="w-4 h-4 mr-2" />
                          Voir détails
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <Edit className="w-4 h-4 mr-2" />
                          Modifier
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <div className="mt-4 space-y-2">
                    {teacher.phone && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Phone className="w-4 h-4" />
                        <span>{teacher.phone}</span>
                      </div>
                    )}
                    {teacher.email && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Mail className="w-4 h-4" />
                        <span className="truncate">{teacher.email}</span>
                      </div>
                    )}
                    {teacher.specialization && (
                      <p className="text-sm text-muted-foreground">
                        Spécialisation: {teacher.specialization}
                      </p>
                    )}
                  </div>

                  {/* Section Compte Utilisateur */}
                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Key className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium">Compte</span>
                      </div>
                      {accountStatus.has_account ? (
                        <div className="flex items-center gap-2">
                          <Badge variant={accountStatus.is_active ? "default" : "secondary"} className="text-xs">
                            {accountStatus.is_active ? (
                              <><UserCheck className="w-3 h-3 mr-1" />Actif</>
                            ) : (
                              <><UserX className="w-3 h-3 mr-1" />Inactif</>
                            )}
                          </Badge>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleAccount(teacher.id)}
                            disabled={isActionLoading}
                            className="h-7 text-xs"
                            data-testid={`toggle-account-${teacher.id}`}
                          >
                            {isActionLoading ? '...' : accountStatus.is_active ? 'Désactiver' : 'Activer'}
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleCreateAccount(teacher.id)}
                          disabled={isActionLoading}
                          className="h-7 text-xs"
                          data-testid={`create-account-${teacher.id}`}
                        >
                          {isActionLoading ? (
                            'Création...'
                          ) : (
                            <><UserPlus className="w-3 h-3 mr-1" />Créer compte</>
                          )}
                        </Button>
                      )}
                    </div>
                    {accountStatus.email && (
                      <p className="text-xs text-muted-foreground mt-1 truncate">
                        {accountStatus.email}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
